import React, { JSX } from "react";
import { ComponentType, ReactElement, ReactNode } from "react";

export type IconPropsType = {
  additionalStyle?: string;
};

export type StudentInfoType = {
  name: string;
  surname: string;
  img: string;
  email: string;
};

export type TableItemType = {
  id: number;
  title: string;
  description: string;
  created_at: string;
  status: boolean;
  category: string;
  administrator_info: StudentInfoType;
};

export type TableEntryType = {
  id: number;
  title: string;
  status: "pending" | "approved" | "rejected";
  table_items: TableItemType[];
};

export type InputFieldType = {
  label?: string;
  id?: string;
  type?: string;
  placeholder: string;
  value?: string | number;
  checked?: boolean;
  handleChange?: (e: any) => void;
  options?: any[];
};

export type TextareaFieldType = {
  label: string;
  id: string;
  value: string;
  onChange: (e: React.ChangeEvent<HTMLTextAreaElement>) => void;
};

export type SwitchOption = {
  value: any;
  label: string;
  icon?: React.FC<IconPropsType>;
};

export type SwitchProps = {
  value: any;
  handleChange: (optionValue: any) => () => void;
  options: SwitchOption[];
  wrapperClassName: string;
  labelClassName?: string;
  switchClassName?: string;
  id: string;
  label: string;
};

export type ClubTypeMeta = {
  clubTypeName: string;
  pageTitle: string;
  leftTabText: string;
  searchText: string;
  tableHeader: string;
};

export type ClubState = {
  selectedCategoryId: string;
  search: string;
  clubType: any;
  clubTypeMeta: ClubTypeMeta;
};

export type MeetupsState = {
  selectedItem: Activity | null;
  selectedDepartmentId: string;
  selectedCategoryId: string;
  selectedStatus: string;
  fromDate: Date | null;
  toDate: Date | null;
  search: string;
};

export type DisciplinaryDefaultCaseTableState = {
  checkedItems?: number[];
  isAddCaseModalOpen?: boolean;
  caseName?: string;
  isAddDemeritModalOpen?: boolean;
  studentsWithDemeritsTriggerFetch?: number;
  selectedDemerit?: Demerit;
};

export type DisciplinaryCasesPageState = {
  triggerDisciplinaryCasesFetch?: number;
};

export type MainUserJsonSavedData = {
  id: number;
  role?: string;
  email: string;
  mobile: string | null;
  lastname: string;
  firstname: string;
  department?: string;
  externalId: string;
  yearOfStudy: number | null;
  organization?: string;
  organizationId: number;
  subOrganization?: string;
  subOrganizationId?: number;
  faculty?: string;
  speciality?: string;
  isInvited?: boolean;
  isConfirmed?: boolean;
  createdAt?: string;
  updatedAt?: string;
};

export type ClubMember = {
  status: number;
  is_admin: number;
};

export type CommonDemeritType = {
  id: number;
  name: string;
};

export type DemeritType = {
  id: number;
  common_demerit_id: number;
  common_demerit_type: CommonDemeritType;
  name: string;
};

export type DemeritMediaObject = {
  label: string;
};

export type DemeritMedia = Media & {
  demerit_media: DemeritMediaObject;
};

export type Demerit = {
  id: number;
  main_user_id: number;
  main_user: MainUser;
  demerit_type_id: number;
  demerit_type: DemeritType;
  description: string;
  status: number;
  createdAt: string;
  updatedAt: string;
  media: DemeritMedia[];
  incident_link: string;
  created_by: number | null;
  students?: MainUser[];
};

export type DisciplinaryCase = {
  id: number;
  name: string;
  status: number;
  createdAt: string;
  demerits: Demerit[];
  created_by: number;
  is_archived: number;
};

export type ClubCategory = {
  id: number;
  name: string;
};

export type ActivityTargetAudience = {
  id: number;
  name: string;
  label: string;
};

export type Role = {
  id: number;
  label: string;
  name: string;
  category_name: string;
};

export interface MainUser {
  id: number;
  external_user_id: number;
  profile_image: string | null;
  json_saved_data: MainUserJsonSavedData;
}

export interface MainUserClub extends MainUser {
  club_members: ClubMember;
}

export interface MainUserWithRoles extends MainUser {
  roles: Role[];
}

export interface MainUserWithDemerits extends MainUser {
  demerits: Demerit[];
}

export type AdditionalSocialTranscriptMerit = {
  id: number;
  key: string;
  name: string;
  description: string;
  points: number;
  media_id: number;
  createdAt: string;
  updatedAt: string;
  main_user_additional_social: number;
  media: Media;
};

export interface MainUserWithDemeritsAndSocialTranscriptItems
  extends MainUserWithDemerits {
  social_transcript_items: SocialTranscriptItem[];
  psychologist_clients?: any[];
  additional_social_transcript_merits?: AdditionalSocialTranscriptMerit[];
  scanned_face_image?: string | null;
  last_matched_at?: string | null;
  createdAt?: string;
  updatedAt?: string;
  clubs?: any[];
  mentor_connections?: any[];
}

export type Club = {
  id: number;
  name: string;
  description: string;
  logo: string;
  club_category_id: number;
  is_main: number;
  status: number;
  members: MainUserClub[];
  category: ClubCategory;
};

export type ActivityComment = {
  by: number;
  type: string;
  content: string;
  createdAt: string;
};

export type Activity = {
  id: number;
  main_image: string;
  department_id: number;
  department: Department;
  category_id: number;
  category: ClubCategory;
  target_audience: ActivityTargetAudience;
  location: string;
  name: string;
  description: string;
  status: number;
  createdAt: Date;
  start_date: string;
  end_date: string;
  created_by: number | null;
  comment?: string;
  reject_comment?: string;
  rejection_comment?: string;
  additional_data?: {
    comments?: readonly ActivityComment[];
    reject_comment?: string;
    rejection_comment?: string;
    comment?: string;
    [key: string]: any;
  };
};

export type Media = {
  id: number;
  filename: string | null;
  link: string;
  createdAt: string;
};

export type SocialSubcategory = {
  id: number;
  social_category_id: number;
  name: string;
  description: string;
  points: number;
  social_category?: SocialCategory;
  transcript_items?: SocialTranscriptItem[];
};

export type SocialCategory = {
  id: number;
  name: string;
  description: string;
  subcategories: SocialSubcategory[];
};

export type SocialTranscriptItem = {
  id: number;
  main_user_id: number;
  social_subcategory_id: string;
  status: number;
  main_user: MainUser;
  social_subcategory: SocialSubcategory;
  media: Media[];
  event_date?: string;
  description?: string;
  link?: string;
  createdAt: string;
};

export type Class = {
  id: number;
  course_id: number;
  name: string;
};

export type Course = {
  id: number;
  main_user_id: number;
  name: string;
  classes: Class[];
};

export type Exam = {
  id: number;
  main_user_id: number;
  name: string;
  course_id: string;
  start_date: Date;
  start_time: string;
  end_time: string;
  status: number;
  course?: Course;
  classes?: Class[];
};

export class ClubFormFields {
  id?: number;
  name: string = "";
  description: string = "";
  logo: File | string = "";
  logoBase64: string = "";
  club_category_id: string = "";
  is_main: number = 0;
  status: number = 1;
  admin_main_user_id: number = 0;
  admin_main_user_data: MainUserJsonSavedData = {} as MainUserJsonSavedData;
  admin_main_user_data_stringified: string = "";
}

export class AddEventFormFields {
  id?: number;
  main_image: File | string = "";
  main_imageBase64: string = "";
  department_id: string = "";
  category_id: string = "";
  start_date: Date | null = null;
  end_date: Date | null = null;
  name: string = "";
  description: string = "";
  status: number = 1;
  activity_target_audience_id: string = "";
  location: string = "";
  expected_reach: string = "";
  accept_applications: boolean = false;
  level: string = "";
}

export const clubFormFields: (keyof ClubFormFields)[] = [
  "id",
  "logo",
  "name",
  "is_main",
  "admin_main_user_id",
  "description",
  "club_category_id",
  "status",
];

export type Department = {
  id: number;
  title: string;
  description: string;
  organization_id: number;
};

export type TableStatusType = "pending" | "approved" | "rejected";

export type MeetupsTableItemType = {
  id: number;
  title: string;
  created_at: string;
  start_date: string;
  status: string;
  category: string;
  img: string;
  department: string;
  descr: string;
  time?: string;
  comment: string;
};

export type SectionType = "title" | "text" | "list";

export interface Section {
  type: SectionType;
  content: string | string[] | undefined;
}

export type ActivityStatusMeta = {
  color: string;
  alt: string;
  image: any;
  label: string;
  additionalRender: () => JSX.Element | null;
};
export type PageSwapperType = {
  additionalStyle?: string;
  btns?: ReactElement[];
  activeBtn?: string;
};
